// COMMUNITY_PAGE_VERSION: v7-wheel
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFeatureFlags } from '../contexts/FeatureFlagContext';
import PokeFriendsModal from '../components/PokeFriendsModal';
import ReportContentModal from '../components/ReportContentModal';
import Snackbar from '@mui/material/Snackbar';
// Auth check via token (no AuthContext in this project)
import {
    Box,
    IconButton,
    Typography,
    CircularProgress,
    Alert,
    Button,
    Card,
    Avatar,
    Chip,
    Tabs,
    Tab,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import PublicIcon from '@mui/icons-material/Public';
import PeopleIcon from '@mui/icons-material/People';
import ShieldIcon from '@mui/icons-material/Shield';
import OutlinedFlagIcon from '@mui/icons-material/OutlinedFlag';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import PersonAddIcon from '@mui/icons-material/PersonAdd';

const API_URL = process.env.REACT_APP_API_URL || 'https://3yslsbk5n1.execute-api.us-east-1.amazonaws.com/prod';

interface Author {
    name: string | null;
    email: string;
    profilePicture: string | null;
}

interface TripPhoto {
    url: string;
    thumbnail?: string;
    visibility?: 'public' | 'private' | 'friends_only';
    caption?: string;
    aiCaption?: string;
    aiTags?: string[];
    aiLocationGuess?: string | null;
    id?: string;
    [key: string]: any;
}

interface AutoShareSettings {
    showCreatorName: boolean;
    showDestination: boolean;
    showTripDates: boolean;
    showItinerary: boolean;
    showPhotos: boolean;
    allowBooking: boolean;
}

interface PhotoItem {
    type: 'photo';
    photoId: string;
    url: string;
    thumbnail: string;
    caption?: string;
    aiCaption?: string;
    aiTags?: string[];
    aiLocationGuess?: string | null;
    uploadedAt: string;
    trip: {
        tripId: string;
        oderId: string;
        title?: string;
        destination?: string;
        startDate?: string;
        endDate?: string;
        visibility: 'public' | 'private' | 'friends_family';
        author?: Author;
        createdAt: string;
    };
    tripVisibility: 'public' | 'private' | 'friends_family';
    userHasFullAccess: boolean;
    autoShareSettings: AutoShareSettings | null;
    sharedBy?: string;
    creatorName?: string;
}

interface Trip {
    tripId: string;
    oderId: string;
    title: string;
    destination: string;
    startDate: string;
    endDate: string;
    description?: string;
    photos?: TripPhoto[] | string[];
    visibility: 'public' | 'private' | 'friends_family';
    likesCount?: number;
    savesCount?: number;
    createdAt: string;
    author: Author;
    isFriendTrip?: boolean;
    sharedBy?: string;
    photoUrl?: string;
    photoId?: string;
    userHasFullAccess?: boolean;
    autoShareSettings?: AutoShareSettings | null;
}

interface SponsoredPost {
    id: string;
    type: 'sponsored';
    partnerName: string;
    headline: string;
    description: string;
    ctaText: string;
    ctaLink: string;
    imageUrl?: string;
    gradientStart: string;
    gradientEnd: string;
}

const getPhotoUrl = (photo: string | TripPhoto): string => {
    if (typeof photo === 'string') {
        return photo;
    }
    return photo.url;
};

const isVideoUrl = (url: string): boolean => {
    if (!url) return false;
    const videoExtensions = ['.mp4', '.mov', '.webm', '.ogg', '.avi', '.m4v'];
    const lowerUrl = url.toLowerCase().split('?')[0]; // Remove query params
    return videoExtensions.some(ext => lowerUrl.endsWith(ext));
};

const getDisplayablePhotos = (photos: (string | TripPhoto)[] | undefined): (string | TripPhoto)[] => {
    if (!photos || photos.length === 0) return [];
    return photos.filter(photo => {
        if (typeof photo === 'string') return true;
        return photo.visibility === 'public' || !photo.visibility;
    });
};

// ---------------------------------------------------------------------------
// Lightbox: full-screen photo viewer with pinch-zoom + bounded 2D pan.
// Opens when a feed photo is tapped. Tap backdrop or press the close button
// or swipe down to dismiss. No double-tap gestures. Zoom/pan always available.
// ---------------------------------------------------------------------------
interface LightboxProps {
    photos: { src: string; alt: string }[];
    startIndex: number;
    onClose: () => void;
}

const Lightbox: React.FC<LightboxProps> = ({ photos, startIndex, onClose }) => {
    const [index, setIndex] = useState(startIndex);
    const [scale, setScale] = useState(1);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const [dragY, setDragY] = useState(0); // live vertical drag for the pager (1x only)

    const imgRef = useRef<HTMLImageElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    // Gesture refs
    const pinchStartDist = useRef<number | null>(null);
    const pinchStartScale = useRef(1);
    const panStart = useRef<{ x: number; y: number } | null>(null);
    const offsetStart = useRef({ x: 0, y: 0 });
    const pagerStartY = useRef<number | null>(null);
    const dragYRef = useRef(0); // live drag value, read in onTouchEnd (avoids stale state)
    const wheelLockRef = useRef(0); // timestamp cooldown so one wheel gesture = one photo

    const current = photos[index];

    // Reset zoom/pan whenever the photo changes.
    useEffect(() => {
        setScale(1);
        setOffset({ x: 0, y: 0 });
        setDragY(0);
    }, [index]);

    // Clamp offset so the (scaled) image can't be dragged past its own edges.
    const clampOffset = useCallback((x: number, y: number, s: number) => {
        const container = containerRef.current;
        const img = imgRef.current;
        if (!container || !img) return { x, y };
        const cw = container.clientWidth;
        const ch = container.clientHeight;
        const natW = img.naturalWidth || cw;
        const natH = img.naturalHeight || ch;
        const fitScale = Math.min(cw / natW, ch / natH);
        const scaledW = natW * fitScale * s;
        const scaledH = natH * fitScale * s;
        const maxX = Math.max(0, (scaledW - cw) / 2);
        const maxY = Math.max(0, (scaledH - ch) / 2);
        return {
            x: Math.max(-maxX, Math.min(maxX, x)),
            y: Math.max(-maxY, Math.min(maxY, y)),
        };
    }, []);

    const distance = (t: TouchList) =>
        Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);

    // Mirror reactive values into refs so the native (non-passive) touch
    // listeners always read current values without re-binding every render.
    const scaleRef = useRef(scale);
    const offsetRef = useRef(offset);
    const idxRef = useRef(index);
    scaleRef.current = scale;
    offsetRef.current = offset;
    idxRef.current = index;

    const SNAP = 60; // px past which a swipe advances to the next/prev photo

    // Attach touch listeners natively as NON-PASSIVE. React's JSX onTouchMove is
    // passive, so preventDefault() there is ignored and iOS hijacks the drag as
    // page scroll — which is why the pager didn't respond. This fixes that.
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const onStart = (e: TouchEvent) => {
            if (e.touches.length === 2) {
                pinchStartDist.current = distance(e.touches);
                pinchStartScale.current = scaleRef.current;
                pagerStartY.current = null;
                panStart.current = null;
            } else if (e.touches.length === 1) {
                if (scaleRef.current > 1) {
                    panStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
                    offsetStart.current = offsetRef.current;
                } else {
                    pagerStartY.current = e.touches[0].clientY;
                }
            }
        };

        const onMove = (e: TouchEvent) => {
            const s = scaleRef.current;
            if (e.touches.length === 2 && pinchStartDist.current !== null) {
                e.preventDefault();
                const next = (distance(e.touches) / pinchStartDist.current) * pinchStartScale.current;
                const clampedScale = Math.max(1, Math.min(4, next));
                setScale(clampedScale);
                setOffset(prev => clampOffset(prev.x, prev.y, clampedScale));
            } else if (e.touches.length === 1 && panStart.current && s > 1) {
                e.preventDefault();
                const dx = e.touches[0].clientX - panStart.current.x;
                const dy = e.touches[0].clientY - panStart.current.y;
                setOffset(clampOffset(offsetStart.current.x + dx, offsetStart.current.y + dy, s));
            } else if (e.touches.length === 1 && pagerStartY.current !== null && s === 1) {
                e.preventDefault();
                const dy = e.touches[0].clientY - pagerStartY.current;
                dragYRef.current = dy;
                setDragY(dy);
            }
        };

        const onEnd = () => {
            const s = scaleRef.current;
            if (pagerStartY.current !== null && s === 1) {
                const finalDrag = dragYRef.current;
                if (finalDrag <= -SNAP) {
                    setIndex(i => (i + 1) % photos.length); // swipe up → next (wraps to first)
                } else if (finalDrag >= SNAP) {
                    setIndex(i => (i - 1 + photos.length) % photos.length); // swipe down → prev (wraps to last)
                } else {
                    setDragY(0); // snap back
                }
            }
            dragYRef.current = 0;
            pinchStartDist.current = null;
            panStart.current = null;
            pagerStartY.current = null;
            if (s <= 1.01) {
                setScale(1);
                setOffset({ x: 0, y: 0 });
            }
        };

        // Mouse wheel pages photos (desktop). Cooldown so one scroll = one photo,
        // and only when not zoomed. Non-passive so preventDefault stops page scroll.
        const onWheel = (e: WheelEvent) => {
            if (scaleRef.current > 1) return; // let zoomed state ignore wheel-paging
            e.preventDefault();
            const now = Date.now();
            if (now - wheelLockRef.current < 350) return;
            if (Math.abs(e.deltaY) < 20) return;
            wheelLockRef.current = now;
            if (e.deltaY > 0) {
                setIndex(i => (i + 1) % photos.length); // scroll down → next
            } else {
                setIndex(i => (i - 1 + photos.length) % photos.length); // scroll up → prev
            }
        };

        el.addEventListener('touchstart', onStart, { passive: false });
        el.addEventListener('touchmove', onMove, { passive: false });
        el.addEventListener('touchend', onEnd, { passive: false });
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => {
            el.removeEventListener('touchstart', onStart);
            el.removeEventListener('touchmove', onMove);
            el.removeEventListener('touchend', onEnd);
            el.removeEventListener('wheel', onWheel);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [photos.length, clampOffset]);

    // Keyboard: Esc closes, arrows page.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
            else if (e.key === 'ArrowDown') setIndex(i => (i + 1) % photos.length);
            else if (e.key === 'ArrowUp') setIndex(i => (i - 1 + photos.length) % photos.length);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose, photos.length]);

    if (!current) return null;

    return (
        <Box
            ref={containerRef}
            sx={{
                position: 'fixed',
                inset: 0,
                zIndex: 1400,
                bgcolor: '#000',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                touchAction: 'none',
                overflow: 'hidden',
            }}
        >
            <IconButton
                onClick={onClose}
                aria-label="Close photo"
                sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    zIndex: 2,
                    color: 'white',
                    bgcolor: 'rgba(0,0,0,0.4)',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
                }}
            >
                <CloseIcon />
            </IconButton>
            <Box
                component="img"
                ref={imgRef}
                src={current.src}
                alt={current.alt}
                draggable={false}
                sx={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    transform: scale > 1
                        ? `translate(${offset.x}px, ${offset.y}px) scale(${scale})`
                        : `translateY(${dragY}px)`,
                    transition: (pinchStartDist.current || panStart.current || pagerStartY.current) ? 'none' : 'transform 0.25s ease',
                    userSelect: 'none',
                    cursor: scale > 1 ? 'grab' : 'default',
                }}
            />
        </Box>
    );
};

const CommunityPage: React.FC = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { isEnabled, isLoading: flagsLoading } = useFeatureFlags();
    const isAuthenticated = !!localStorage.getItem('idToken');
    const communityDiscoverEnabled = isEnabled('community_public');
    const communityFriendsEnabled = isEnabled('community_friends');
    const adsEnabled = isEnabled('advertisements_global') === true;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [searchParams] = useSearchParams();
    const [trips, setTrips] = useState<Trip[]>([]);
    const [friendsTrips, setFriendsTrips] = useState<Trip[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [hasMore, setHasMore] = useState(true);
    const [lastEvaluatedKey, setLastEvaluatedKey] = useState<string | null>(null);
    const [likedTrips, setLikedTrips] = useState<Set<string>>(new Set());
    const [savedTrips, setSavedTrips] = useState<Set<string>>(new Set());
    const [activeTab, setActiveTab] = useState(0);
    const [hasFriends, setHasFriends] = useState<boolean | null>(null);
    const [pokeOpen, setPokeOpen] = useState(false);
    const [pokeSuccess, setPokeSuccess] = useState('');
    const [reportOpen, setReportOpen] = useState(false);
    const [reportPhotoId, setReportPhotoId] = useState<string | null>(null);
    // Lightbox: the index into the photo-only list that's open, or null when closed.
    const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

    const initialFetchDone = useRef(false);
    const sentinelRef = useRef<HTMLDivElement>(null);

    // Fetch friends count to differentiate empty state messages
    useEffect(() => {
        if (!isAuthenticated) return;
        const token = localStorage.getItem('idToken');
        if (!token) return;
        fetch(`${API_URL}/friends/list`, {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${token}` },
        })
            .then(r => {
                if (r.status === 401) {
                    localStorage.removeItem('idToken');
                    localStorage.removeItem('accessToken');
                    localStorage.removeItem('refreshToken');
                    setHasFriends(null);
                    return null;
                }
                return r.json();
            })
            .then(data => {
                if (!data) return;
                if (data.success && data.friends) {
                    const active = data.friends.filter((f: any) => f.status === 'active');
                    setHasFriends(active.length > 0);
                } else {
                    setHasFriends(false);
                }
            })
            .catch(() => setHasFriends(false));
    }, [isAuthenticated]);

    const fetchTrips = useCallback(async (reset: boolean = false, tabOverride?: number) => {
        try {
            const currentTab = tabOverride !== undefined ? tabOverride : activeTab;
            const tab = currentTab === 0 ? 'public' : 'friends';
            const params = new URLSearchParams({ limit: '10', tab: tab });

            if (!reset && lastEvaluatedKey) {
                params.append('lastKey', lastEvaluatedKey);
            }

            const headers: Record<string, string> = { 'Content-Type': 'application/json' };
            const token = localStorage.getItem('idToken');
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${API_URL}/trips/public?${params.toString()}`, {
                method: 'GET',
                headers,
            });

            if (!response.ok) {
                throw new Error('Failed to fetch trips');
            }

            const data = await response.json();
            const items: PhotoItem[] = data.items || [];
            const transformedTrips: Trip[] = items.map((item: PhotoItem) => ({
                tripId: item.trip?.tripId || item.photoId,
                oderId: item.trip?.oderId || '',
                title: item.trip?.title || item.caption || 'Shared Photo',
                destination: item.trip?.destination || '',
                startDate: item.trip?.startDate || '',
                endDate: item.trip?.endDate || '',
                visibility: item.tripVisibility,
                createdAt: item.uploadedAt || item.trip?.createdAt || '',
                author: item.trip?.author || { name: item.creatorName || null, email: '', profilePicture: null },
                photos: [{
                    url: item.url,
                    thumbnail: item.thumbnail,
                    visibility: 'public',
                    id: item.photoId,
                    aiCaption: (item as any).aiCaption,
                    aiTags: (item as any).aiTags,
                    aiLocationGuess: (item as any).aiLocationGuess,
                }],
                photoUrl: item.url,
                photoId: item.photoId,
                isFriendTrip: item.tripVisibility === 'friends_family',
                sharedBy: item.sharedBy,
                userHasFullAccess: item.userHasFullAccess,
                autoShareSettings: item.autoShareSettings,
            }));

            if (currentTab === 0) {
                setTrips(prev => reset ? transformedTrips : [...prev, ...transformedTrips]);
            } else {
                setFriendsTrips(prev => reset ? transformedTrips : [...prev, ...transformedTrips]);
            }

            setLastEvaluatedKey(data.lastEvaluatedKey || null);
            setHasMore(data.hasMore ?? false);
        } catch (err) {
            console.error('Error fetching trips:', err);
            setError('Failed to load trips. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [lastEvaluatedKey, activeTab]);

    // Initial fetch + tab-from-URL
    useEffect(() => {
        if (initialFetchDone.current || flagsLoading) return;
        initialFetchDone.current = true;
        const tabParam = searchParams.get('tab');
        if (tabParam === 'friends' && communityFriendsEnabled) {
            const targetTab = communityDiscoverEnabled ? 1 : 0;
            setActiveTab(targetTab);
            fetchTrips(true, communityDiscoverEnabled ? 1 : 1);
            return;
        }
        if (communityDiscoverEnabled) {
            setActiveTab(0);
            fetchTrips(true, 0);
        } else if (communityFriendsEnabled) {
            setActiveTab(0);
            fetchTrips(true, 1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [flagsLoading, communityDiscoverEnabled, communityFriendsEnabled]);

    // Determine which data to display based on flags and active tab
    const getDisplayTrips = () => {
        if (communityDiscoverEnabled && communityFriendsEnabled) {
            return activeTab === 0 ? trips : friendsTrips;
        }
        if (communityDiscoverEnabled) {
            return trips;
        }
        if (communityFriendsEnabled) {
            return friendsTrips;
        }
        return [];
    };
    const displayTrips = getDisplayTrips();

    // Photo-only list the full-screen pager flows through (skips sponsored cards).
    // Each entry is the full-resolution image + its index, aligned to displayTrips order.
    const photoList = displayTrips
        .map((trip) => {
            const url = trip.photoUrl || (trip.photos && trip.photos.length > 0 ? getPhotoUrl(trip.photos[0]) : null);
            return url && !isVideoUrl(url) ? { src: url, alt: trip.title } : null;
        })
        .filter((p): p is { src: string; alt: string } => p !== null);

    const handleTabChange = useCallback((newTab: number) => {
        if (newTab === activeTab) return;
        setActiveTab(newTab);
        setLoading(true);
        setLastEvaluatedKey(null);
        const targetTrips = newTab === 0 ? trips : friendsTrips;
        if (targetTrips.length === 0) {
            fetchTrips(true, newTab);
        } else {
            setLoading(false);
        }
    }, [activeTab, trips, friendsTrips, fetchTrips]);

    const handleLike = (tripId: string) => {
        if (!isAuthenticated) {
            navigate(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
            return;
        }
        setLikedTrips(prev => {
            const newSet = new Set(prev);
            if (newSet.has(tripId)) newSet.delete(tripId);
            else newSet.add(tripId);
            return newSet;
        });
    };

    const handleSave = (tripId: string) => {
        if (!isAuthenticated) {
            navigate(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
            return;
        }
        setSavedTrips(prev => {
            const newSet = new Set(prev);
            if (newSet.has(tripId)) newSet.delete(tripId);
            else newSet.add(tripId);
            return newSet;
        });
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    };

    const sponsoredPosts: SponsoredPost[] = [
        {
            id: 'sponsor-1',
            type: 'sponsored',
            partnerName: 'Viator',
            headline: '🎭 Skip the Lines in Paris',
            description: 'Book top-rated tours & activities from $25',
            ctaText: 'Explore Tours',
            ctaLink: 'https://viator.com',
            gradientStart: '#2D6E6A',
            gradientEnd: '#00D4B8',
        },
        {
            id: 'sponsor-2',
            type: 'sponsored',
            partnerName: 'GetYourGuide',
            headline: '🏛️ Local Experiences Worldwide',
            description: 'Discover hidden gems with local guides',
            ctaText: 'Book Now',
            ctaLink: 'https://getyourguide.com',
            gradientStart: '#FF6B6B',
            gradientEnd: '#FF8787',
        },
    ];

    const getMixedContent = (): (Trip | SponsoredPost)[] => {
        const mixed: (Trip | SponsoredPost)[] = [];
        let sponsorIndex = 0;
        displayTrips.forEach((trip, index) => {
            mixed.push(trip);
            if (adsEnabled && activeTab === 0 && (index + 1) % 4 === 0 && sponsorIndex < sponsoredPosts.length) {
                mixed.push(sponsoredPosts[sponsorIndex]);
                sponsorIndex++;
            }
        });
        return mixed;
    };
    const mixedContent = getMixedContent();

    // Infinite scroll: load more when the sentinel enters view
    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel) return;
        const io = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && hasMore && !loading) {
                fetchTrips(false);
            }
        }, { rootMargin: '600px' });
        io.observe(sentinel);
        return () => io.disconnect();
    }, [hasMore, loading, fetchTrips]);

    // ----- Render: a single feed card (photo at natural aspect ratio) -----
    const renderTripCard = (trip: Trip, index: number) => {
        const isLiked = likedTrips.has(trip.tripId);
        const isSaved = savedTrips.has(trip.tripId);
        const firstPhotoUrl = trip.photoUrl || (trip.photos && trip.photos.length > 0 ? getPhotoUrl(trip.photos[0]) : null);
        const firstThumbUrl = (trip.photos && typeof trip.photos[0] === 'object' && (trip.photos[0] as any).thumbnail) || firstPhotoUrl;
        const displayablePhotos = getDisplayablePhotos(trip.photos);
        const isVideo = firstPhotoUrl ? isVideoUrl(firstPhotoUrl) : false;

        const userHasFullAccess = trip.userHasFullAccess ?? false;
        const showItineraryButton = userHasFullAccess && (trip.autoShareSettings?.showItinerary ?? true);
        const bookingEnabled = isEnabled('booking_enabled') === true;
        const showBookButton = bookingEnabled && userHasFullAccess && (trip.autoShareSettings?.allowBooking ?? true);

        const aiCaption = (trip.photos && typeof trip.photos[0] === 'object') ? (trip.photos[0] as any).aiCaption : null;
        const aiTags = (trip.photos && typeof trip.photos[0] === 'object') ? (trip.photos[0] as any).aiTags : null;

        return (
            <Card
                key={`${trip.tripId}-${trip.photoId || index}`}
                sx={{
                    borderRadius: 3,
                    overflow: 'hidden',
                    bgcolor: '#fff',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                    mb: 2,
                }}
            >
                {/* Author header */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                            src={trip.author?.profilePicture || undefined}
                            sx={{ width: 40, height: 40 }}
                        >
                            {trip.author?.name?.[0] || ''}
                        </Avatar>
                        <Box>
                            {trip.author?.name && (
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                                    {trip.author.name}
                                </Typography>
                            )}
                            {(trip.isFriendTrip || trip.sharedBy) && (
                                <Chip
                                    icon={<PeopleIcon sx={{ fontSize: 14, color: '#2D6E6A !important' }} />}
                                    label={trip.sharedBy ? `Shared by ${trip.sharedBy}` : 'Friend'}
                                    size="small"
                                    sx={{ bgcolor: 'rgba(45,110,106,0.1)', color: '#2D6E6A', fontWeight: 600, fontSize: '0.7rem', height: 22, mt: 0.25 }}
                                />
                            )}
                        </Box>
                    </Box>
                    {userHasFullAccess && (
                        <IconButton onClick={() => navigate(`/trips/${trip.tripId}`)} sx={{ color: '#2D6E6A' }} aria-label="View trip">
                            <InfoOutlinedIcon />
                        </IconButton>
                    )}
                </Box>

                {/* Photo at natural aspect ratio. Tap to open the lightbox. */}
                {firstPhotoUrl && (
                    isVideo ? (
                        <Box sx={{ width: '100%', bgcolor: '#000', display: 'flex', justifyContent: 'center' }}>
                            <video
                                src={firstPhotoUrl}
                                muted
                                loop
                                playsInline
                                controls
                                style={{ width: '100%', maxHeight: '80vh', objectFit: 'contain' }}
                            />
                        </Box>
                    ) : (
                        <Box
                            component="img"
                            src={firstThumbUrl}
                            alt={trip.title}
                            loading="lazy"
                            decoding="async"
                            onClick={() => {
                                if (!firstPhotoUrl) return;
                                const pos = photoList.findIndex(p => p.src === firstPhotoUrl);
                                setLightboxIndex(pos >= 0 ? pos : 0);
                            }}
                            sx={{
                                width: '100%',
                                height: 'auto',
                                display: 'block',
                                maxHeight: '85vh',
                                objectFit: 'contain',
                                bgcolor: '#f2f2f2',
                                cursor: 'zoom-in',
                            }}
                        />
                    )
                )}

                {/* Action row — always visible */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, pt: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton onClick={() => handleLike(trip.tripId)} aria-label="Like" sx={{ color: '#333' }}>
                            {isLiked ? <FavoriteIcon sx={{ color: '#FF6B6B' }} /> : <FavoriteBorderIcon />}
                        </IconButton>
                        <Typography variant="body2">{(trip.likesCount || 0) + (isLiked ? 1 : 0)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton onClick={() => handleSave(trip.tripId)} aria-label="Save" sx={{ color: '#333' }}>
                            {isSaved ? <BookmarkIcon sx={{ color: '#E0A800' }} /> : <BookmarkBorderIcon />}
                        </IconButton>
                        <Typography variant="body2">{(trip.savesCount || 0) + (isSaved ? 1 : 0)}</Typography>
                    </Box>
                    <IconButton
                        onClick={() => { if (trip.photoId) { setReportPhotoId(trip.photoId); setReportOpen(true); } }}
                        sx={{ color: '#333', ml: 'auto' }}
                        title="Report this photo"
                        aria-label="Report photo"
                    >
                        <OutlinedFlagIcon />
                    </IconButton>
                </Box>

                {/* Caption / meta */}
                <Box sx={{ px: 2, pb: 2, pt: 0.5 }}>
                    {trip.title && trip.title !== 'Shared Photo' && (
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.25 }}>{trip.title}</Typography>
                    )}
                    {aiCaption && (
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', mb: 0.5 }}>{aiCaption}</Typography>
                    )}
                    {trip.destination && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <LocationOnOutlinedIcon sx={{ fontSize: 18, color: '#2D6E6A' }} />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{trip.destination}</Typography>
                        </Box>
                    )}
                    {trip.startDate && trip.endDate && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <CalendarTodayOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                {formatDate(trip.startDate)} - {formatDate(trip.endDate)}
                            </Typography>
                        </Box>
                    )}
                    {trip.description && (
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                            {trip.description}
                        </Typography>
                    )}
                    {aiTags && aiTags.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                            {aiTags.slice(0, 8).map((tag: string, idx: number) => (
                                <Box key={idx} sx={{ px: 1, py: 0.25, bgcolor: 'rgba(45,110,106,0.1)', color: '#2D6E6A', borderRadius: 1, fontSize: '0.7rem', fontWeight: 500 }}>
                                    {tag}
                                </Box>
                            ))}
                        </Box>
                    )}
                    {displayablePhotos.length > 1 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                            <PhotoLibraryIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{displayablePhotos.length} photos</Typography>
                        </Box>
                    )}
                    {(showItineraryButton || showBookButton) && (
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                            {showItineraryButton && (
                                <Button variant="outlined" size="small" onClick={() => navigate(`/trips/${trip.tripId}`)}
                                    sx={{ borderColor: '#2D6E6A', color: '#2D6E6A', borderRadius: '20px', textTransform: 'none', px: 2 }}>
                                    See Itinerary
                                </Button>
                            )}
                            {showBookButton && (
                                <Button variant="contained" size="small" onClick={() => navigate(`/book?from=community&tripId=${trip.tripId}`)}
                                    sx={{ bgcolor: '#2D6E6A', color: 'white', borderRadius: '20px', textTransform: 'none', px: 2, '&:hover': { bgcolor: '#009688' } }}>
                                    Book This Trip
                                </Button>
                            )}
                        </Box>
                    )}
                </Box>
            </Card>
        );
    };

    // ----- Render: a sponsored card in the feed -----
    const renderSponsoredPost = (post: SponsoredPost) => (
        <Card
            key={post.id}
            sx={{
                borderRadius: 3,
                overflow: 'hidden',
                mb: 2,
                background: `linear-gradient(135deg, ${post.gradientStart} 0%, ${post.gradientEnd} 100%)`,
                p: 4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
            }}
        >
            <Chip label="Sponsored" sx={{ bgcolor: 'rgba(255,255,255,0.3)', color: 'white', mb: 2 }} />
            <Typography variant="h5" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>{post.headline}</Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)', mb: 2 }}>{post.description}</Typography>
            <Button variant="contained" href={post.ctaLink} target="_blank"
                sx={{ bgcolor: 'white', color: post.gradientStart, borderRadius: '30px', px: 4, py: 1, fontWeight: 600, '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' } }}>
                {post.ctaText}
            </Button>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', mt: 1.5 }}>Partner: {post.partnerName}</Typography>
        </Card>
    );

    // ----- Disabled state -----
    if (!flagsLoading && !communityDiscoverEnabled && !communityFriendsEnabled) {
        return (
            <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: '#fff', p: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>Community is currently unavailable</Typography>
                <Typography variant="body1" sx={{ opacity: 0.7, textAlign: 'center' }}>
                    The community features are disabled. Please check back later.
                </Typography>
                <Button variant="contained" onClick={() => navigate('/trips')}
                    sx={{ mt: 3, bgcolor: '#2D6E6A', borderRadius: '30px', px: 4, '&:hover': { bgcolor: '#009688' } }}>
                    Go to My Trips
                </Button>
            </Box>
        );
    }

    // ----- Loading state -----
    if ((loading || flagsLoading) && displayTrips.length === 0) {
        return (
            <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fff' }}>
                <CircularProgress sx={{ color: '#2D6E6A' }} />
            </Box>
        );
    }

    const showTabs = communityDiscoverEnabled && communityFriendsEnabled;

    return (
        <>
            <Box sx={{ minHeight: '100vh', bgcolor: '#fafafa' }}>
                {/* Sticky top bar: tabs + close. Sits above the feed, never over a photo. */}
                <Box
                    sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                        bgcolor: 'rgba(255,255,255,0.95)',
                        backdropFilter: 'blur(8px)',
                        borderBottom: '1px solid rgba(0,0,0,0.08)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 2,
                        py: 1,
                    }}
                >
                    {showTabs ? (
                        <Tabs
                            value={activeTab}
                            onChange={(_, v) => handleTabChange(v)}
                            sx={{
                                minHeight: 40,
                                '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 40 },
                                '& .Mui-selected': { color: '#2D6E6A !important' },
                                '& .MuiTabs-indicator': { backgroundColor: '#2D6E6A' },
                            }}
                        >
                            <Tab icon={<PublicIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Discover" />
                            <Tab icon={<PeopleIcon sx={{ fontSize: 18 }} />} iconPosition="start" label={friendsTrips.length > 0 ? `Friends (${friendsTrips.length})` : 'Friends'} />
                        </Tabs>
                    ) : (
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#2D6E6A' }}>
                            {communityDiscoverEnabled ? 'Discover' : 'Friends'}
                        </Typography>
                    )}
                    <IconButton
                        onClick={() => { const token = localStorage.getItem('idToken'); navigate(token ? '/home' : '/'); }}
                        aria-label="Close community"
                        sx={{ color: '#333' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* Feed */}
                <Box sx={{ maxWidth: 600, mx: 'auto', px: { xs: 1, sm: 2 }, py: 2 }}>
                    {mixedContent.length === 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', py: 10, px: 3 }}>
                            {activeTab === 0 ? (
                                <>
                                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>No trips to discover yet</Typography>
                                    <Typography variant="body1" sx={{ mb: 3, opacity: 0.8 }}>Be the first to share a trip!</Typography>
                                    <Button variant="contained" onClick={() => navigate('/trips/new')}
                                        sx={{ bgcolor: '#2D6E6A', borderRadius: '30px', px: 4, '&:hover': { bgcolor: '#009688' } }}>
                                        Create a Trip
                                    </Button>
                                </>
                            ) : (
                                <>
                                    {isAuthenticated ? (
                                        <>
                                            <PeopleIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
                                            {hasFriends ? (
                                                <>
                                                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>No shared trips yet</Typography>
                                                    <Typography variant="body1" sx={{ mb: 3, opacity: 0.8 }}>
                                                        Your friends haven't shared any trips yet. Check back soon!
                                                    </Typography>
                                                    <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => setPokeOpen(true)}
                                                        sx={{ bgcolor: '#2D6E6A', borderRadius: '30px', px: 4, '&:hover': { bgcolor: '#009688' } }}>
                                                        Poke Friends to Share
                                                    </Button>
                                                </>
                                            ) : (
                                                <>
                                                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>No friends yet</Typography>
                                                    <Typography variant="body1" sx={{ mb: 3, opacity: 0.8 }}>
                                                        Connect with friends to see their shared trips and photos here.
                                                    </Typography>
                                                    <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => navigate('/friends')}
                                                        sx={{ bgcolor: '#2D6E6A', borderRadius: '30px', px: 4, '&:hover': { bgcolor: '#009688' } }}>
                                                        Find Friends
                                                    </Button>
                                                </>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            <ShieldIcon sx={{ fontSize: 64, mb: 2, color: '#2D6E6A' }} />
                                            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>Your travel memories, privately shared</Typography>
                                            <Typography variant="body1" sx={{ mb: 3, opacity: 0.8 }}>
                                                On the Friends tab, trips and photos are only visible to connected friends — this is by design.
                                                Public photos are available on the Discover tab. Sign up to connect with people you know and share your adventures privately.
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                                                <Button variant="outlined" onClick={() => navigate('/login')}
                                                    sx={{ borderColor: '#2D6E6A', color: '#2D6E6A', borderRadius: '30px', px: 4 }}>
                                                    Log In
                                                </Button>
                                                <Button variant="contained" onClick={() => navigate('/signup')}
                                                    sx={{ bgcolor: '#2D6E6A', borderRadius: '30px', px: 4, '&:hover': { bgcolor: '#009688' } }}>
                                                    Sign Up Free
                                                </Button>
                                            </Box>
                                        </>
                                    )}
                                </>
                            )}
                        </Box>
                    ) : (
                        <>
                            {mixedContent.map((item, index) => {
                                if ('type' in item && item.type === 'sponsored') {
                                    return renderSponsoredPost(item as SponsoredPost);
                                }
                                return renderTripCard(item as Trip, index);
                            })}
                            {/* Infinite-scroll sentinel */}
                            <Box ref={sentinelRef} sx={{ height: 1 }} />
                            {loading && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                    <CircularProgress size={28} sx={{ color: '#2D6E6A' }} />
                                </Box>
                            )}
                        </>
                    )}
                </Box>

                {error && (
                    <Alert severity="error" sx={{ position: 'fixed', bottom: 16, left: 16, right: 16, zIndex: 20, maxWidth: 600, mx: 'auto' }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}
            </Box>

            {lightboxIndex !== null && photoList.length > 0 && (
                <Lightbox photos={photoList} startIndex={lightboxIndex} onClose={() => setLightboxIndex(null)} />
            )}

            <PokeFriendsModal
                open={pokeOpen}
                onClose={() => setPokeOpen(false)}
                onSuccess={(count) => { setPokeSuccess(`Poke sent to ${count} friend${count !== 1 ? 's' : ''}!`); }}
            />
            <ReportContentModal
                open={reportOpen}
                onClose={() => { setReportOpen(false); setReportPhotoId(null); }}
                contentType="photo"
                contentId={reportPhotoId || ''}
                contentLabel="this photo"
            />
            <Snackbar
                open={!!pokeSuccess}
                autoHideDuration={4000}
                onClose={() => setPokeSuccess('')}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setPokeSuccess('')} severity="success" sx={{ width: '100%' }}>
                    {pokeSuccess}
                </Alert>
            </Snackbar>
        </>
    );
};

export default CommunityPage;
